import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { qs } from "@/lib/api";
import { fmt } from "@/lib/utils-msip";
import { pctTone } from "@/lib/dpc";
import { TrendingUp, TrendingDown, Minus, MapPinned } from "lucide-react";

const PACE = {
  ahead: { label: "Ahead", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: TrendingUp },
  on: { label: "On track", cls: "bg-amber-50 text-amber-700 border-amber-200", icon: Minus },
  behind: { label: "Behind", cls: "bg-red-50 text-red-700 border-red-200", icon: TrendingDown },
};

function ZoneCard({ z, active, rank, onPick }) {
  const r = z.retail;
  const p = PACE[r.pace];
  const Icon = p?.icon;
  const safe = z.key.replace(/[^A-Za-z0-9]/g, "-");
  return (
    <button type="button" onClick={onPick} data-testid={`dpc-zone-card-${safe}`} aria-pressed={active}
      className={`group text-left shrink-0 w-[230px] rounded-md border bg-card p-3 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${active ? "ring-2 ring-foreground border-foreground" : "hover:border-foreground/40"}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">#{rank} · zone</div>
          <div className="font-heading text-sm font-bold truncate" title={z.label}>{z.label}</div>
          <div className="text-[10px] text-muted-foreground">{z.dealers} location{z.dealers === 1 ? "" : "s"} · {z.approved_plans}/{z.dealers} plans approved</div>
        </div>
        {p && <span className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-semibold ${p.cls}`} data-testid={`dpc-zone-pace-${safe}`}><Icon size={10} />{p.label}</span>}
      </div>
      <div className="mt-2 flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Retail</div>
          <div className="num text-xl font-extrabold leading-none" data-testid={`dpc-zone-retail-${safe}`}>{fmt(r.ach)} <span className="text-xs font-medium text-muted-foreground">/ {fmt(Math.round(r.plan_td))}</span></div>
        </div>
        <div className={`num text-lg font-bold ${pctTone(r.pct)}`} data-testid={`dpc-zone-pct-${safe}`}>{r.pct != null ? `${r.pct}%` : "—"}</div>
      </div>
      <div className="mt-2 flex items-center justify-between text-[11px] text-muted-foreground num" data-testid={`dpc-zone-gap-${safe}`}>
        <span>Gap <span className={`font-semibold ${r.gap > 0 ? "text-red-600" : "text-emerald-700"}`}>{r.gap > 0 ? r.gap : r.gap === 0 ? 0 : `+${Math.abs(r.gap)}`}</span></span>
        <span>Proj {fmt(r.projected)} / {fmt(r.month_plan)}</span>
        <span>Need {r.required_rate != null ? `${r.required_rate}/day` : "—"}</span>
      </div>
    </button>
  );
}

// Zone comparison strip — every zone side by side; click a card to drill the cockpit into that zone.
export function ZoneStrip({ month, period, model, source, active }) {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!month) return;
    api.get(`/dpc/dashboard/zones?${qs({ month, period, model, source })}`).then(({ data }) => setD(data)).catch(() => setD(null));
  }, [month, period, model, source]);
  if (!d || d.zones.length < 2) return null;
  return (
    <div data-testid="dpc-zone-strip">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-heading text-sm font-semibold flex items-center gap-1.5"><MapPinned size={14} /> Zones side by side <span className="text-xs text-muted-foreground font-normal">retail vs plan-to-date · ranked by % · click a zone to drill in</span></h3>
        {active && <button type="button" onClick={() => navigate("/dpc")} className="text-xs underline text-muted-foreground hover:text-foreground" data-testid="dpc-zone-strip-all">Back to all locations</button>}
      </div>
      <div className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1">
        {d.zones.map((z, i) => <ZoneCard key={z.key} z={z} rank={i + 1} active={active === z.key} onPick={() => navigate(active === z.key ? "/dpc" : `/dpc?zone=${encodeURIComponent(z.key)}`)} />)}
      </div>
    </div>
  );
}
